// C11 (R6, design horario-turno-dia §6, recorte de S6c/S6d): diálogo
// "Horario por día" de UN turno. Presentacional puro — el contenedor
// (`C11ConfigTurnosPage`) resuelve la llamada a la API.

import { useState } from 'react';
import { diasSemanaOrdenados, type HorarioSemanaDia, type HorarioSemanaDiaInput } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';

const NOMBRES_DIA = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

interface FilaHorario {
  dia_semana: number;
  no_opera: boolean;
  hora_inicio: string;
  hora_fin: string;
}

function filasDesde(dias: HorarioSemanaDia[]): FilaHorario[] {
  const porDia = new Map(dias.map((d) => [d.dia_semana, d]));
  return Array.from({ length: 7 }, (_, i) => {
    const dia_semana = i + 1;
    const d = porDia.get(dia_semana);
    return {
      dia_semana,
      no_opera: d?.no_opera ?? false,
      hora_inicio: d?.hora_inicio ?? '06:00',
      hora_fin: d?.hora_fin ?? '14:00',
    };
  });
}

export interface HorarioPorDiaDialogProps {
  turnoNombre: string;
  diaInicioSemana: number;
  dias: HorarioSemanaDia[];
  base: { hora_inicio: string; hora_fin: string };
  /** Próximo día operativo (min del input y default). */
  aplicarDesdeDefault: string;
  guardando: boolean;
  serverError: string | null;
  /** Programaciones (líderes+supervisores) futuras en días ahora sin turno. `null` = sin guardar aún. */
  programacionesAfectadas: number | null;
  onGuardar: (body: { dias: HorarioSemanaDiaInput[]; aplicar_desde: string }) => void;
  onClose: () => void;
}

export function HorarioPorDiaDialog({
  turnoNombre,
  diaInicioSemana,
  dias,
  base,
  aplicarDesdeDefault,
  guardando,
  serverError,
  programacionesAfectadas,
  onGuardar,
  onClose,
}: HorarioPorDiaDialogProps) {
  const [filas, setFilas] = useState<FilaHorario[]>(() => filasDesde(dias));
  const [aplicarDesde, setAplicarDesde] = useState(aplicarDesdeDefault);
  const orden = diasSemanaOrdenados(diaInicioSemana);

  const setFila = (dia: number, patch: Partial<FilaHorario>) => {
    setFilas((prev) => prev.map((f) => (f.dia_semana === dia ? { ...f, ...patch } : f)));
  };

  const submit = () => {
    const body: HorarioSemanaDiaInput[] = filas.map((f) =>
      f.no_opera
        ? { dia_semana: f.dia_semana, no_opera: true }
        : {
            dia_semana: f.dia_semana,
            no_opera: false,
            hora_inicio: f.hora_inicio,
            hora_fin: f.hora_fin,
          },
    );
    onGuardar({ dias: body, aplicar_desde: aplicarDesde });
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Horario por día · {turnoNombre}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <Table>
            <THead>
              <TR>
                <TH>Día</TH>
                <TH align="center">No opera</TH>
                <TH align="right">Inicio</TH>
                <TH align="right">Fin</TH>
                <TH />
              </TR>
            </THead>
            <TBody>
              {orden.map((dia) => {
                const f = filas.find((x) => x.dia_semana === dia)!;
                const nombre = NOMBRES_DIA[dia]!;
                return (
                  <TR key={dia}>
                    <TD className="text-fg">{nombre}</TD>
                    <TD align="center">
                      <input
                        type="checkbox"
                        aria-label={`${nombre} no opera`}
                        checked={f.no_opera}
                        className="size-4"
                        onChange={(ev) => setFila(dia, { no_opera: ev.target.checked })}
                      />
                    </TD>
                    <TD align="right">
                      <Input
                        type="time"
                        aria-label={`${nombre} hora inicio`}
                        value={f.hora_inicio}
                        disabled={f.no_opera}
                        onChange={(ev) => setFila(dia, { hora_inicio: ev.target.value })}
                      />
                    </TD>
                    <TD align="right">
                      <Input
                        type="time"
                        aria-label={`${nombre} hora fin`}
                        value={f.hora_fin}
                        disabled={f.no_opera}
                        onChange={(ev) => setFila(dia, { hora_fin: ev.target.value })}
                      />
                    </TD>
                    <TD align="right">
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={f.no_opera}
                        onClick={() =>
                          setFila(dia, { hora_inicio: base.hora_inicio, hora_fin: base.hora_fin })
                        }
                      >
                        Igual al horario base · {nombre}
                      </Button>
                    </TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>

          <div className="flex flex-col gap-1">
            <Label htmlFor="hpd-aplicar-desde">Aplicar a partir de</Label>
            <Input
              id="hpd-aplicar-desde"
              type="date"
              value={aplicarDesde}
              min={aplicarDesdeDefault}
              onChange={(ev) => setAplicarDesde(ev.target.value)}
            />
          </div>
          <p className="text-[11px] text-fg-tertiary">
            Este cambio no modifica registros anteriores.
          </p>

          {serverError && (
            <p role="alert" className="text-sm text-crit">
              {serverError}
            </p>
          )}
          {programacionesAfectadas !== null && programacionesAfectadas > 0 && (
            <p className="text-sm text-warn">
              {programacionesAfectadas === 1
                ? '1 programación futura quedó en un día sin turno.'
                : `${programacionesAfectadas} programaciones futuras quedaron en un día sin turno.`}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} loading={guardando}>
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
